// ============================================================
// OILMED — Hooks React (Firestore temps réel)
// ============================================================
// Utilisation dans les composants :
//
//   const { stations, loading } = useStations();
//   const { stocks }            = useTerminalStocks('SEP');
//   const { commandes }         = useCommandes();
//   const { journal }           = useJournal();
// ============================================================

import { useState, useEffect } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  watchTerminalStocks,
  watchStations,
  watchCommandes,
  watchLivraisonsOrphelines,
  watchJournal,
  watchFournisseurs,
  calcEspaceDisponible,
} from "./oilmed.service";

// ════════════════════════════════════════════════════════════
// useTerminalStocks — Stocks sous douane + LP d'un terminal
// ════════════════════════════════════════════════════════════
/**
 * @param {'SEP'|'LP'} terminalId
 * @returns {{
 *   stocks: { essence: StockProduit, go: StockProduit },
 *   loading: boolean,
 *   error: string|null
 * }}
 */
export function useTerminalStocks(terminalId) {
  const [stocks, setStocks] = useState({});
  const [loading, setLoading] = useState(true);
  const [authUid, setAuthUid] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(getAuth(), (user) =>
      setAuthUid(user?.uid ?? null),
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!terminalId || !authUid) return;
    setLoading(true);
    const unsub = watchTerminalStocks(terminalId, (data) => {
      setStocks(data);
      setLoading(false);
    });
    return () => unsub();
  }, [terminalId, authUid]);

  return { stocks, loading };
}

// ════════════════════════════════════════════════════════════
// useAllTerminaux — Stocks des deux terminaux en une fois
// ════════════════════════════════════════════════════════════
/**
 * @returns {{
 *   terminaux: { SEP: {...}, LP: {...} },
 *   loading: boolean
 * }}
 */
export function useAllTerminaux() {
  const { stocks: stocksSEP, loading: l1 } = useTerminalStocks("SEP");
  const { stocks: stocksLP, loading: l2 } = useTerminalStocks("LP");

  return {
    terminaux: { SEP: stocksSEP, LP: stocksLP },
    loading: l1 || l2,
  };
}

// ════════════════════════════════════════════════════════════
// useStations — Toutes les stations en temps réel
// ════════════════════════════════════════════════════════════
/**
 * @returns {{
 *   stations: { lumumba: Station, limete: Station, barumbu: Station },
 *   stationsArray: Station[],
 *   loading: boolean,
 *   alertes: string[]   — stations sous seuil
 * }}
 */
export function useStations(produit = "essence") {
  const [stations, setStations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = watchStations((data) => {
      setStations(data);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Alertes automatiques — stations sous seuil pour le produit actif
  const alertes = Object.values(stations)
    .filter((s) => (s.stock?.[produit] ?? 0) <= (s.seuil?.[produit] ?? 0))
    .map((s) => ({
      stationId: s.id,
      label: s.label,
      stock: s.stock?.[produit] ?? 0,
      seuil: s.seuil?.[produit] ?? 0,
      vide: (s.stock?.[produit] ?? 0) === 0,
    }));

  const stationsArray = Object.values(stations);

  return { stations, stationsArray, loading, alertes };
}

// ════════════════════════════════════════════════════════════
// useCommandes — Toutes les commandes en temps réel
// ════════════════════════════════════════════════════════════
/**
 * @param {{start:Date,end:Date}|null} [monthRange] - Si fourni, limite aux commandes créées dans ce mois
 * @returns {{
 *   commandes: Commande[],          — toutes (filtrées par terminal/produit si fournis)
 *   commandesActives: Commande[],   — statut != 'cloture'
 *   loading: boolean
 * }}
 */
export function useCommandes(filtreTerminal = null, filtreProduit = null, monthRange = null) {
  const [commandes, setCommandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const startMs = monthRange?.start?.getTime() ?? null;
  const endMs = monthRange?.end?.getTime() ?? null;

  useEffect(() => {
    setLoading(true);
    const unsub = watchCommandes((data) => {
      setCommandes(data);
      setLoading(false);
    }, monthRange);
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startMs, endMs]);

  let filtered = commandes;
  if (filtreTerminal)
    filtered = filtered.filter((c) => c.terminal === filtreTerminal);
  if (filtreProduit)
    filtered = filtered.filter((c) => c.produit === filtreProduit);

  const commandesActives = filtered.filter((c) => c.statut !== "cloture");

  return { commandes: filtered, commandesActives, loading };
}

// ════════════════════════════════════════════════════════════
// useLivraisonsOrphelines — Livraisons sans commande (par terminal)
// ════════════════════════════════════════════════════════════
/**
 * @param {'SEP'|'LP'} terminalId
 * @param {string} [filtreProduit] - 'essence' | 'go'
 * @returns {{ orphelines: LivraisonOrpheline[], loading: boolean }}
 */
export function useLivraisonsOrphelines(terminalId, filtreProduit = null) {
  const [orphelines, setOrphelines] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!terminalId) return;
    setLoading(true);
    const unsub = watchLivraisonsOrphelines(terminalId, (data) => {
      setOrphelines(data);
      setLoading(false);
    });
    return () => unsub();
  }, [terminalId]);

  const filtered = filtreProduit
    ? orphelines.filter((o) => o.produit === filtreProduit)
    : orphelines;

  return { orphelines: filtered, loading };
}

// ════════════════════════════════════════════════════════════
// useFournisseurs — Fournisseurs du libre pratique (config/gestock)
// ════════════════════════════════════════════════════════════
/**
 * @param {'SEP'|'LP'} [terminalId] - si fourni, `liste` = fournisseurs du terminal
 * @returns {{ parTerminal: Object<string,string[]>, liste: string[] }}
 */
// Couleurs des fournisseurs (cartes, badges) — ni rouge (négatif) ni or
// (non attribué). Attribuées dans l'ordre des listes SEP puis LP : un
// fournisseur garde la même couleur sur toutes les pages et terminaux.
const COULEURS_FOURNISSEURS = [
  "#2F54EB", "#FA8C16", "#13C2C2", "#722ED1",
  "#EB2F96", "#52C41A", "#A0522D", "#1677FF",
];

export function useFournisseurs(terminalId = null) {
  const [parTerminal, setParTerminal] = useState({});
  useEffect(() => watchFournisseurs(setParTerminal), []);
  const ordre = [];
  for (const t of ["SEP", "LP", ...Object.keys(parTerminal)]) {
    for (const f of parTerminal[t] || []) if (!ordre.includes(f)) ordre.push(f);
  }
  const couleur = (nom) => {
    const i = ordre.indexOf(nom);
    if (i >= 0) return COULEURS_FOURNISSEURS[i % COULEURS_FOURNISSEURS.length];
    let h = 0; // fournisseur hors liste (ex. ancien lot) : couleur stable par nom
    for (const c of String(nom || "")) h = (h * 31 + c.charCodeAt(0)) >>> 0;
    return COULEURS_FOURNISSEURS[h % COULEURS_FOURNISSEURS.length];
  };
  return {
    parTerminal,
    liste: terminalId ? parTerminal[terminalId] || [] : [],
    couleur,
  };
}

// ════════════════════════════════════════════════════════════
// useJournal — Journal des mouvements en temps réel
// ════════════════════════════════════════════════════════════
/**
 * @param {number} limite
 * @param {{start:Date,end:Date}|null} [monthRange] - Si fourni, limite aux entrées créées dans ce mois
 * @returns {{ journal: JournalEntry[], loading: boolean }}
 */
export function useJournal(limite = 100, monthRange = null) {
  const [journal, setJournal] = useState([]);
  const [loading, setLoading] = useState(true);
  const startMs = monthRange?.start?.getTime() ?? null;
  const endMs = monthRange?.end?.getTime() ?? null;

  useEffect(() => {
    setLoading(true);
    const unsub = watchJournal((data) => {
      setJournal(data);
      setLoading(false);
    }, limite, monthRange);
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [limite, startMs, endMs]);

  return { journal, loading };
}

// ════════════════════════════════════════════════════════════
// useCapacitesCommande — Calcul espaces disponibles par station
// ════════════════════════════════════════════════════════════
/**
 * Retourne pour chaque station l'espace disponible pour un produit.
 * Utilisé dans le formulaire de commande pour afficher les max commandables
 * et bloquer si dépassement.
 *
 * @param {string} produit - 'essence' | 'go'
 * @returns {{
 *   capacites: {
 *     [stationId]: {
 *       label: string,
 *       capacite: number,
 *       stockActuel: number,
 *       espaceLibre: number,   ← max commandable pour cette station
 *     }
 *   },
 *   loading: boolean
 * }}
 */
export function useCapacitesCommande(produit) {
  const { stations, loading } = useStations(produit);

  const capacites = {};
  Object.entries(stations).forEach(([id, sta]) => {
    capacites[id] = {
      label: sta.label,
      ...calcEspaceDisponible(sta, produit),
    };
  });

  return { capacites, loading };
}

// ════════════════════════════════════════════════════════════
// useDashboard — Toutes les données pour le tableau de bord
// ════════════════════════════════════════════════════════════
/**
 * Agrège toutes les données nécessaires pour la vue tableau de bord.
 */
export function useDashboard(terminalId, produit) {
  const { stocks } = useTerminalStocks(terminalId);
  const { stations, loading } = useStations(produit);
  const { commandesActives } = useCommandes(terminalId, produit);

  const stockProduit = stocks?.[produit] ?? { sd: 0, lp: 0, sd_init: 0 };

  // Total restant à livrer sur les commandes actives
  const totalBLRestant = commandesActives.reduce(
    (acc, c) => acc + Math.max(0, (c.qtyCom || 0) - (c.totalLivre || 0)),
    0,
  );

  // Calculate real-time alerts based on citernes data
  // Note: This requires citernesMap which is not available here
  // For now, use the old alerts but they will be fixed when citernes data is integrated
  const alertes = [];

  // Total stock en stations (will need citernes data for accuracy)
  const totalStations = Object.values(stations).reduce(
    (a, s) => a + (s.stock?.[produit] ?? 0),
    0,
  );

  return {
    stockProduit,
    totalBLRestant,
    totalStations,
    alertes,
    loading,
  };
}
